import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext';

const API_URL = '/api/admin/advances/';
const STAFF_URL = '/api/admin/staff/';

const formatMoney = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeMoneyInput = (value) => {
    const cleaned = String(value ?? '').replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) return cleaned;
    return `${parts[0]}.${parts.slice(1).join('')}`;
};

const stripMoneyFormatting = (value) => String(value ?? '').replace(/,/g, '');

const handleMoneyFocusValue = (value) => stripMoneyFormatting(value);

const handleMoneyBlurValue = (value) => formatMoney(value || 0);

const Advances = () => {
    const { hasPermission } = usePermissions();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [staffOptions, setStaffOptions] = useState([]);

    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [selectedStaff, setSelectedStaff] = useState('');

    const [formData, setFormData] = useState({
        staff: '',
        amount: '',
        payroll_month: new Date().getMonth() + 1,
        payroll_year: new Date().getFullYear(),
        remarks: '',
    });

    const canCreate = hasPermission('ADVANCES', 'can_create');
    const canEdit = hasPermission('ADVANCES', 'can_edit');
    const canDelete = hasPermission('ADVANCES', 'can_delete');

    const MONTH_OPTIONS = [
        { id: '', text: 'All Months' },
        { id: 1, text: 'January' }, { id: 2, text: 'February' }, { id: 3, text: 'March' },
        { id: 4, text: 'April' }, { id: 5, text: 'May' }, { id: 6, text: 'June' },
        { id: 7, text: 'July' }, { id: 8, text: 'August' }, { id: 9, text: 'September' },
        { id: 10, text: 'October' }, { id: 11, text: 'November' }, { id: 12, text: 'December' }
    ];

    const YEAR_OPTIONS = useMemo(() => {
        const y = new Date().getFullYear();
        return [
            { id: y, text: y.toString() },
            { id: y + 1, text: (y + 1).toString() },
        ];
    }, []);

    const totalAmount = useMemo(() => (
        records.reduce((acc, record) => acc + Number(record.amount || 0), 0)
    ), [records]);

    const { items: sortedRecords, requestSort, getSortIndicator } = useSortableTable(records);

    const fetchStaff = async () => {
        try {
            const res = await fetch(`${STAFF_URL}?page_size=1000`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch staff');
            const data = await res.json();
            const staff = data.results || data;
            setStaffOptions(staff.map(item => ({ id: item.id, text: `${item.first_name} ${item.last_name}`.trim() || item.name })));
        } catch (error) {
            toast.error(error.message);
        }
    };

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedStaff) params.append('staff_id', selectedStaff);
            if (filterMonth) params.append('month', filterMonth);
            if (filterYear) params.append('year', filterYear);

            const res = await fetch(`${API_URL}?${params.toString()}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch advances');
            const data = await res.json();
            setRecords(data.results || data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
        fetchRecords();
    }, []);

    const resetForm = () => {
        setFormData({
            staff: '',
            amount: '',
            payroll_month: new Date().getMonth() + 1,
            payroll_year: new Date().getFullYear(),
            remarks: '',
        });
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const endpoint = editingId ? `${API_URL}${editingId}/` : API_URL;
            const method = editingId ? 'PATCH' : 'POST';
            const res = await fetch(endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify({
                    ...formData,
                    amount: stripMoneyFormatting(formData.amount),
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.detail || data.amount?.[0] || data.staff?.[0] || 'Failed to save advance');
            }

            toast.success(editingId ? 'Advance updated successfully' : 'Advance created successfully');
            setShowModal(false);
            resetForm();
            fetchRecords();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        setFormData({
            staff: record.staff,
            amount: formatMoney(record.amount),
            payroll_month: record.payroll_month,
            payroll_year: record.payroll_year,
            remarks: record.remarks || '',
        });
        setShowModal(true);
    };

    const handleDelete = async (recordId) => {
        if (!window.confirm('Delete this advance record?')) return;
        try {
            const res = await fetch(`${API_URL}${recordId}/`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete advance');
            toast.success('Advance deleted');
            fetchRecords();
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>Manage Advances</h2>
                <button className="btn btn-primary" disabled={!canCreate} onClick={() => { resetForm(); setShowModal(true); }}>
                    <i className="fa fa-plus"></i> New Advance
                </button>
            </div>

            <div className="filters-container" style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ width: 220 }}>
                    <label>Staff</label>
                    <Select2Dropdown options={[{ id: '', text: 'All Staff' }, ...staffOptions]} value={selectedStaff} onChange={setSelectedStaff} displayKey="text" valueKey="id" />
                </div>
                <div style={{ width: 160 }}>
                    <label>Payroll Month</label>
                    <Select2Dropdown options={MONTH_OPTIONS} value={filterMonth} onChange={setFilterMonth} displayKey="text" valueKey="id" />
                </div>
                <div style={{ width: 120 }}>
                    <label>Payroll Year</label>
                    <Select2Dropdown options={YEAR_OPTIONS} value={filterYear} onChange={setFilterYear} displayKey="text" valueKey="id" />
                </div>
                <div>
                    <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={fetchRecords}>Load Records</button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('staff_name')} style={{ cursor: 'pointer' }}>Staff {getSortIndicator('staff_name')}</th>
                            <th onClick={() => requestSort('amount')} style={{ cursor: 'pointer' }}>Amount {getSortIndicator('amount')}</th>
                            <th onClick={() => requestSort('payroll_period_display')} style={{ cursor: 'pointer' }}>Applied Payroll Period {getSortIndicator('payroll_period_display')}</th>
                            <th onClick={() => requestSort('applied_in_payroll')} style={{ cursor: 'pointer' }}>Applied in Payroll {getSortIndicator('applied_in_payroll')}</th>
                            <th onClick={() => requestSort('remarks')} style={{ cursor: 'pointer' }}>Remarks {getSortIndicator('remarks')}</th>
                            <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Created On {getSortIndicator('created_at')}</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && records.length === 0 && (
                            <tr><td colSpan={7} className="no-data">No advances found.</td></tr>
                        )}
                        {sortedRecords.map(record => (
                            <tr key={record.id}>
                                <td>{record.staff_name}</td>
                                <td>{formatMoney(record.amount)}</td>
                                <td>{record.payroll_period_display || `${MONTH_OPTIONS.find(m => m.id === Number(record.payroll_month))?.text || record.payroll_month} ${record.payroll_year}`}</td>
                                <td>
                                    <span className={`badge badge-${record.applied_in_payroll ? 'success' : 'warning'}`}>
                                        {record.applied_in_payroll ? 'Applied' : 'Pending'}
                                    </span>
                                </td>
                                <td>{record.remarks || '-'}</td>
                                <td>{new Date(record.created_at).toLocaleDateString()}</td>
                                <td>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {canEdit && <button className="btn btn-sm btn-primary" onClick={() => handleEdit(record)}>Edit</button>}
                                        {canDelete && <button className="btn btn-sm btn-danger" onClick={() => handleDelete(record.id)}>Delete</button>}
                                        {!canEdit && !canDelete && <span className="text-muted">No CRUD permission</span>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!loading && records.length > 0 && (
                            <tr style={{ backgroundColor: '#f6f8fa', fontWeight: 600 }}>
                                <td>Total</td>
                                <td>{formatMoney(totalAmount)}</td>
                                <td colSpan={5}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '560px', padding: 0, overflow: 'hidden' }}>
                        <button className="modal-close-btn" onClick={() => { setShowModal(false); resetForm(); }} style={{ zIndex: 10 }}>&times;</button>

                        <div className="dialog-container" style={{ boxShadow: 'none', border: 'none', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="dialog-header">
                                <h3>{editingId ? 'Edit Advance' : 'Add New Advance'}</h3>
                            </div>

                            <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
                                <form id="advance-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="required">Staff</label>
                                        <Select2Dropdown
                                            options={staffOptions}
                                            value={formData.staff}
                                            onChange={(value) => setFormData(prev => ({ ...prev, staff: value }))}
                                            displayKey="text"
                                            valueKey="id"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="required">Advance Amount</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.amount || ''}
                                            onChange={e => setFormData(prev => ({ ...prev, amount: normalizeMoneyInput(e.target.value) }))}
                                            onFocus={e => setFormData(prev => ({ ...prev, amount: handleMoneyFocusValue(e.target.value) }))}
                                            onBlur={e => setFormData(prev => ({ ...prev, amount: handleMoneyBlurValue(e.target.value) }))}
                                            required
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 form-group">
                                            <label className="required">Payroll Month</label>
                                            <Select2Dropdown
                                                options={MONTH_OPTIONS.filter(m => m.id !== '')}
                                                value={formData.payroll_month}
                                                onChange={(value) => setFormData(prev => ({ ...prev, payroll_month: value }))}
                                                displayKey="text"
                                                valueKey="id"
                                            />
                                        </div>
                                        <div className="col-md-6 form-group">
                                            <label className="required">Payroll Year</label>
                                            <Select2Dropdown
                                                options={YEAR_OPTIONS}
                                                value={formData.payroll_year}
                                                onChange={(value) => setFormData(prev => ({ ...prev, payroll_year: value }))}
                                                displayKey="text"
                                                valueKey="id"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Remarks</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={formData.remarks}
                                            onChange={e => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                        />
                                    </div>
                                </form>
                            </div>

                            <div className="dialog-footer">
                                <button type="submit" form="advance-form" className="dialog-btn dialog-btn-primary">{editingId ? 'Update Advance' : 'Create Advance'}</button>
                                <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Advances;
